/**
 * PremiumServiceLead — formularz lead dla Usług Premium APISAI
 *
 * Wejście z HOME (prawa kolumna "Usługi Premium").
 * Query param `?usluga=strona-premium` preselektuje typ usługi.
 * POST → /api/premium-leads → wpada do APISAI sales inbox.
 *
 * DNA: Navy / Orange, zero fioletu, storytelling, soft cards.
 */
import { useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Globe2, Palette, MapPin, TrendingUp, Sparkles, Dog, PawPrint,
  ArrowRight, ArrowLeft, Check, Loader2, Phone, Mail, User as UserIcon,
} from 'lucide-react';
import StudioShell from '@/components/studio/StudioShell';
import { useAuth } from '@/context/AuthContext';
import { API } from '@/utils/api';

const SERVICES = [
  { key: 'strona-premium',        label: 'Strona Premium',        icon: Globe2,     desc: 'Profesjonalna strona hodowli zaprojektowana przez nasz zespół.' },
  { key: 'grafika-hodowli',       label: 'Grafika hodowli',       icon: Palette,    desc: 'Spójna identyfikacja wizualna i grafiki do social media.' },
  { key: 'grafika-reproduktora',  label: 'Grafika reproduktora',  icon: Dog,        desc: 'Karta i grafiki prezentujące Twojego reproduktora.' },
  { key: 'grafika-miotu',         label: 'Grafika miotu',         icon: PawPrint,   desc: 'Materiały promocyjne dla aktualnego miotu.' },
  { key: 'wizytowka-google',      label: 'Wizytówka Google',      icon: MapPin,     desc: 'Konfiguracja i optymalizacja wizytówki w Google.' },
  { key: 'kampania-meta',         label: 'Kampania Meta',         icon: TrendingUp, desc: 'Kampania reklamowa na Facebooku i Instagramie.' },
  { key: 'inne',                  label: 'Inne',                  icon: Sparkles,   desc: 'Powiedz nam, w czym możemy pomóc.' },
];

const headers = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function PremiumServiceLead() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const initialService = params.get('usluga');

  const [serviceType, setServiceType] = useState(
    SERVICES.some((s) => s.key === initialService) ? initialService : 'strona-premium'
  );
  const [form, setForm] = useState({
    name: user?.name || '',
    phone: '',
    email: user?.email || '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const activeService = useMemo(
    () => SERVICES.find((s) => s.key === serviceType) || SERVICES[0],
    [serviceType]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error('Podaj imię i nazwisko');
      return;
    }
    if (!form.phone.trim() && !form.email.trim()) {
      toast.error('Podaj telefon lub email, żebyśmy mogli się odezwać');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/premium-leads`,
        { ...form, service_type: serviceType },
        { headers: headers() }
      );
      setDone(true);
      toast.success('Wysłane. Odezwiemy się wkrótce.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Coś poszło nie tak. Spróbuj ponownie.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <StudioShell mode="workspace" activeKey="home" contextLabel="Usługi Premium">
      <main className="flex-1 overflow-y-auto" data-testid="premium-lead-main">
        <div className="max-w-4xl mx-auto px-4 sm:px-8 py-10 sm:py-14">
          {/* Back */}
          <button
            onClick={() => navigate('/panel')}
            data-testid="premium-lead-back"
            className="inline-flex items-center gap-1.5 text-[var(--apisai-text-muted)] hover:text-[var(--apisai-text-primary)] text-sm font-medium mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Wróć do startu
          </button>

          {done ? (
            <div
              className="apisai-soft-card p-8 sm:p-12 text-center max-w-xl mx-auto"
              data-testid="premium-lead-success"
            >
              <div className="w-16 h-16 rounded-2xl bg-[var(--apisai-success-soft)] flex items-center justify-center mx-auto mb-5">
                <Check className="w-8 h-8 text-[var(--apisai-success)]" strokeWidth={2.5} />
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[var(--apisai-text-primary)] mb-2">
                Dziękujemy! 🧡
              </h1>
              <p className="text-[var(--apisai-text-secondary)] text-base leading-relaxed mb-6">
                Twoje zgłoszenie w sprawie <span className="font-semibold text-[var(--apisai-text-primary)]">{activeService.label}</span> trafiło do naszego zespołu.
                Odezwiemy się wkrótce na podany kontakt.
              </p>
              <button
                onClick={() => navigate('/panel')}
                data-testid="premium-lead-success-home"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-[var(--apisai-primary)] hover:bg-[var(--apisai-primary-600)] text-white text-sm font-semibold transition-colors shadow-[var(--shadow-primary)]"
              >
                Wróć do panelu
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <>
              {/* Header */}
              <div className="space-y-3 mb-8">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[var(--apisai-primary)]/12 border border-[var(--apisai-primary-ring)]">
                  <Sparkles className="w-3.5 h-3.5 text-[var(--apisai-primary)]" />
                  <span className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[var(--apisai-primary)]">
                    Usługi Premium APISAI
                  </span>
                </div>
                <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-[var(--apisai-text-primary)] leading-[1.1]">
                  Powiedz nam, czego potrzebujesz
                </h1>
                <p className="text-[var(--apisai-text-secondary)] text-base max-w-xl">
                  Nasz zespół przygotuje dla Ciebie wycenę i odezwie się z konkretami. Bez zobowiązań.
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.1fr] gap-6 lg:gap-10 items-start">
                {/* LEFT — service picker */}
                <div className="space-y-2.5" data-testid="premium-lead-services">
                  <p className="text-[11px] uppercase tracking-wider font-semibold text-[var(--apisai-text-muted)] mb-3">
                    Wybierz usługę
                  </p>
                  {SERVICES.map((s) => {
                    const Icon = s.icon;
                    const active = s.key === serviceType;
                    return (
                      <button
                        key={s.key}
                        type="button"
                        onClick={() => setServiceType(s.key)}
                        data-testid={`premium-service-${s.key}`}
                        className={[
                          'w-full text-left flex items-start gap-3 p-3.5 rounded-2xl border transition-all',
                          active
                            ? 'bg-[var(--apisai-primary)]/[0.07] border-[var(--apisai-primary-ring)] ring-1 ring-[var(--apisai-primary-ring)]'
                            : 'bg-white border-[var(--polish-border)] hover:border-[var(--apisai-primary-ring)] hover:-translate-y-0.5',
                        ].join(' ')}
                      >
                        <div
                          className="flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center"
                          style={{
                            background: active ? 'var(--apisai-primary)' : 'var(--apisai-primary-soft, rgba(249,115,22,0.10))',
                          }}
                        >
                          <Icon
                            className="w-5 h-5"
                            style={{ color: active ? '#fff' : 'var(--apisai-primary)' }}
                            strokeWidth={1.8}
                          />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-[var(--apisai-text-primary)] text-sm font-semibold leading-tight">{s.label}</p>
                          <p className="text-[var(--apisai-text-muted)] text-xs mt-0.5 leading-relaxed">{s.desc}</p>
                        </div>
                        {active && (
                          <Check className="w-4 h-4 text-[var(--apisai-primary)] flex-shrink-0 mt-1" strokeWidth={3} />
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* RIGHT — contact form */}
                <form onSubmit={handleSubmit} className="apisai-soft-card p-6 sm:p-7 space-y-5" data-testid="premium-lead-form">
                  <div className="flex items-center gap-3 pb-1">
                    <div className="w-11 h-11 rounded-xl bg-[var(--apisai-primary)] flex items-center justify-center">
                      <activeService.icon className="w-5 h-5 text-white" strokeWidth={1.8} />
                    </div>
                    <div>
                      <p className="text-[var(--apisai-text-primary)] text-base font-bold leading-tight">{activeService.label}</p>
                      <p className="text-[var(--apisai-text-muted)] text-xs">Wypełnij — zajmie 30 sekund.</p>
                    </div>
                  </div>

                  <div>
                    <label className="text-[var(--apisai-text-secondary)] text-sm font-medium mb-1.5 flex items-center gap-1.5">
                      <UserIcon className="w-3.5 h-3.5" /> Imię i nazwisko
                    </label>
                    <input
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="Anna Kowalska"
                      data-testid="premium-lead-name"
                      className="w-full h-12 px-4 rounded-xl bg-[var(--polish-bg-soft)] border border-[var(--polish-border)] text-[var(--apisai-text-primary)] placeholder:text-[var(--apisai-text-muted)] focus:outline-none focus:border-[var(--apisai-primary)] transition-colors"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-[var(--apisai-text-secondary)] text-sm font-medium mb-1.5 flex items-center gap-1.5">
                        <Phone className="w-3.5 h-3.5" /> Telefon
                      </label>
                      <input
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        placeholder="+48 600 000 000"
                        data-testid="premium-lead-phone"
                        className="w-full h-12 px-4 rounded-xl bg-[var(--polish-bg-soft)] border border-[var(--polish-border)] text-[var(--apisai-text-primary)] placeholder:text-[var(--apisai-text-muted)] focus:outline-none focus:border-[var(--apisai-primary)] transition-colors"
                      />
                    </div>
                    <div>
                      <label className="text-[var(--apisai-text-secondary)] text-sm font-medium mb-1.5 flex items-center gap-1.5">
                        <Mail className="w-3.5 h-3.5" /> Email
                      </label>
                      <input
                        type="email"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        placeholder="anna@email.pl"
                        data-testid="premium-lead-email"
                        className="w-full h-12 px-4 rounded-xl bg-[var(--polish-bg-soft)] border border-[var(--polish-border)] text-[var(--apisai-text-primary)] placeholder:text-[var(--apisai-text-muted)] focus:outline-none focus:border-[var(--apisai-primary)] transition-colors"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-[var(--apisai-text-secondary)] text-sm font-medium mb-1.5 block">
                      W czym możemy pomóc?
                    </label>
                    <textarea
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      placeholder="Napisz kilka słów o tym, czego potrzebujesz..."
                      rows={3}
                      data-testid="premium-lead-message"
                      className="w-full px-4 py-3 rounded-xl bg-[var(--polish-bg-soft)] border border-[var(--polish-border)] text-[var(--apisai-text-primary)] placeholder:text-[var(--apisai-text-muted)] focus:outline-none focus:border-[var(--apisai-primary)] transition-colors resize-none"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="premium-lead-submit"
                    className="w-full inline-flex items-center justify-center gap-2 h-12 rounded-xl bg-[var(--apisai-primary)] hover:bg-[var(--apisai-primary-600)] text-white text-base font-semibold transition-colors shadow-[var(--shadow-primary)] disabled:opacity-60"
                  >
                    {submitting ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        Wyślij zapytanie
                        <ArrowRight className="w-4 h-4" />
                      </>
                    )}
                  </button>
                  <p className="text-[var(--apisai-text-muted)] text-xs text-center">
                    Kontakt bez zobowiązań. Odpowiadamy zwykle w ciągu 24h.
                  </p>
                </form>
              </div>
            </>
          )}
        </div>
      </main>
    </StudioShell>
  );
}
